import { toLonLat, fromLonLat } from 'ol/proj';
import { Overlay } from 'ol';
import Chart from 'chart.js/auto';

const tooltipPlugin = Chart.registry.getPlugin('tooltip');
tooltipPlugin.positioners.synced = function(elements, eventPosition) {
  const chart = this.chart;
  const x = chart.$syncTooltipX ?? eventPosition.x;
  const y = chart.$syncTooltipY ?? eventPosition.y;
  return {
    x: Math.min(x + 90, chart.width - 10),
    y,
    xAlign: 'left',
    yAlign: 'center',
  };
};

const COLLAPSED_H = 48;
const TABLE_H = 374;
const CHART_H = 580;
const HOURS_TO_SHOW = 168; // 7 days
const KP_REFRESH_MS = 15 * 60 * 1000;
const TEMP_LAPSE_C_PER_M = 0.0065;
const WIND_SHEAR_EXPONENT = 0.143;
const CHART_COUNT = 6;
const CHART_MIN_H = 115;
const CHART_MAX_H = 145;

// ── State ─────────────────────────────────────────────────────
let weatherData = null;
let kpData = null;
let isExpanded = false;
let displayMode = 'table'; // 'table' | 'chart'
let currentCoords = { lat: 46.8182, lon: 8.2275 };
let debounceTimer = null;
let kpRefreshTimer = null;
let mapRef = null;
let chartInstances = [];
let crosshairOverlay = null;
let pendingRaf = null;
let syncingCharts = false;
let hasSelectedWeatherLocation = false;
let weatherRequestId = 0;
let droneClass = 'dji_mini';

// ── Drone flight score ────────────────────────────────────────
const DRONE_CLASSES = {
  dji_mini:  {
    label: 'DJI Mini', sublabel: 'Mini 2 / 3 / 4 Pro · < 249g · Level 5 wind',
    windOk: 15, windCaution: 25, windDifficult: 35,
    gustOk: 20, gustCaution: 30, gustDifficult: 38,
  },
  dji_air: {
    label: 'DJI Air', sublabel: 'Air 2S / Air 3 · ~700g · Level 6 wind',
    windOk: 18, windCaution: 28, windDifficult: 43,
    gustOk: 22, gustCaution: 34, gustDifficult: 48,
  },
  dji_mavic: {
    label: 'DJI Mavic', sublabel: 'Mavic 3 / Classic / Pro · ~900g · Level 6 wind',
    windOk: 20, windCaution: 32, windDifficult: 43,
    gustOk: 25, gustCaution: 38, gustDifficult: 48,
  },
  fpv5inch: {
    label: 'FPV 5"', sublabel: '5-inch freestyle / racing · not waterproof',
    windOk: 25, windCaution: 45, windDifficult: 60,
    gustOk: 30, gustCaution: 55, gustDifficult: 70,
    rainOk: 5, rainCaution: 15, rainDifficult: 30,
  },
  avata: {
    label: 'DJI Avata', sublabel: 'Avata 1 / 2 · cinewhoop · ~400g · Level 5 wind',
    windOk: 15, windCaution: 25, windDifficult: 35,
    gustOk: 20, gustCaution: 30, gustDifficult: 38,
  },
};

const SCORE_LABELS = ['', '🔴 Unsafe', '🟠 Difficult', '🟡 Flyable', '🟢 Excellent'];
const SCORE_COLORS = ['', '#ef4444', '#f97316', '#facc15', '#4ade80'];
const SCORE_DOTS   = ['', '🔴', '🟠', '🟡', '🟢'];

function flightScore(h, i) {
  const t = DRONE_CLASSES[droneClass];
  const kpEntry = kpForLocalTime(h.time[i]);
  return Math.min(
    scoreByT(h.windspeed_10m?.[i] ?? 0,  t.windOk, t.windCaution, t.windDifficult),
    scoreByT(h.windspeed_80m?.[i] ?? 0,  t.windOk, t.windCaution, t.windDifficult),
    scoreByT(h.windgusts_10m?.[i] ?? 0,  t.gustOk, t.gustCaution, t.gustDifficult),
    flightScoreRain(h.precipitation_probability?.[i] ?? 0),
    flightScoreCloud(h.cloudcover?.[i] ?? 0),
    flightScoreTemp(h.temperature_2m?.[i] ?? 20),
    kpEntry ? flightScoreKp(kpEntry.kp) : 4,
  );
}
function scoreByT(v, ok, caution, difficult) { return v < ok ? 4 : v < caution ? 3 : v < difficult ? 2 : 1; }
function flightScoreRain(p) {
  const t = DRONE_CLASSES[droneClass];
  return scoreByT(p, t.rainOk ?? 20, t.rainCaution ?? 40, t.rainDifficult ?? 70);
}
function flightScoreCloud(p) { return p < 25 ? 4 : p < 60 ? 3 : 2; }
function flightScoreTemp(t)  {
  if (t < -5 || t > 45) return 1;
  if ((t >= -5 && t < 0) || (t > 40)) return 2;
  if ((t >= 0 && t < 5) || (t > 35)) return 3;
  return 4;
}
function flightScoreKp(kp)   { return kp < 3 ? 4 : kp < 5 ? 3 : kp < 7 ? 2 : 1; }

function scoreTooltipHTML(h, i) {
  const t = DRONE_CLASSES[droneClass];
  const kpEntry = kpForLocalTime(h.time[i]);
  const kp = kpEntry ? kpEntry.kp : null;
  const wind10 = h.windspeed_10m?.[i] ?? 0;
  const wind80 = h.windspeed_80m?.[i] ?? 0;
  const gust   = h.windgusts_10m?.[i] ?? 0;
  const rain   = h.precipitation_probability?.[i] ?? 0;
  const cloud  = h.cloudcover?.[i] ?? 0;
  const temp   = h.temperature_2m?.[i] ?? 20;
  const factors = [
    { label: 'Wind 10m', value: `${Math.round(wind10)} km/h`, score: scoreByT(wind10, t.windOk, t.windCaution, t.windDifficult) },
    { label: 'Wind 80m', value: `${Math.round(wind80)} km/h`, score: scoreByT(wind80, t.windOk, t.windCaution, t.windDifficult) },
    { label: 'Gusts',    value: `${Math.round(gust)} km/h`,   score: scoreByT(gust, t.gustOk, t.gustCaution, t.gustDifficult) },
    { label: 'Rain',     value: `${Math.round(rain)}%`,       score: flightScoreRain(rain) },
    { label: 'Cloud',    value: `${Math.round(cloud)}%`,      score: flightScoreCloud(cloud) },
    { label: 'Temp',     value: `${Math.round(temp)}°C`,      score: flightScoreTemp(temp) },
    { label: 'Kp index', value: kp != null ? kp.toFixed(1) : '?', score: kp != null ? flightScoreKp(kp) : 4 },
  ];
  const overall = Math.min(...factors.map(f => f.score));
  const rows = factors.map(f => {
    const worst = f.score === overall && overall < 4;
    return `<div class="st-row${worst ? ' st-worst' : ''}"><span class="st-dot">${SCORE_DOTS[f.score]}</span><span class="st-lbl">${f.label}</span><span class="st-val">${f.value}</span></div>`;
  }).join('');
  const p = DRONE_CLASSES[droneClass];
  return `<div class="st-head" style="color:${SCORE_COLORS[overall]}">${SCORE_LABELS[overall]}</div><div class="st-drone">${p.label} · ${p.sublabel}</div>${rows}`;
}

let scoreTooltipEl = null;
function ensureScoreTooltip() {
  if (!scoreTooltipEl) {
    scoreTooltipEl = document.createElement('div');
    scoreTooltipEl.id = 'score-tooltip';
    document.body.appendChild(scoreTooltipEl);
  }
  return scoreTooltipEl;
}
function showScoreTooltip(anchor, h, i) {
  const el = ensureScoreTooltip();
  el.innerHTML = scoreTooltipHTML(h, i);
  el.style.display = 'block';
  const r = anchor.getBoundingClientRect();
  el.style.left = r.left + 'px';
  el.style.right = 'auto';
  el.style.top = 'auto';
  el.style.bottom = (window.innerHeight - r.top + 8) + 'px';
  requestAnimationFrame(() => {
    if (!scoreTooltipEl) return;
    const tr = scoreTooltipEl.getBoundingClientRect();
    if (tr.right > window.innerWidth - 8) scoreTooltipEl.style.left = Math.max(8, window.innerWidth - tr.width - 8) + 'px';
  });
}
function hideScoreTooltip() {
  if (scoreTooltipEl) scoreTooltipEl.style.display = 'none';
}

// ── Init ──────────────────────────────────────────────────────
export function initWeather(map) {
  mapRef = map;
  buildWeatherBarHTML();
  buildInfoModal();
  buildCrosshair(map);

  const center = map.getView().getCenter();
  const [lon, lat] = toLonLat(center);
  currentCoords = { lat, lon };
  updateLocationDisplay();

  map.on('moveend', () => {
    if (hasSelectedWeatherLocation) return;
    const c = map.getView().getCenter();
    const [lo, la] = toLonLat(c);
    currentCoords = { lat: la, lon: lo };
    updateLocationDisplay();
    updateCrosshair();
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchWeather, 2000);
  });

  Chart.defaults.color = '#aaa';
  Chart.defaults.borderColor = 'rgba(255,255,255,0.07)';
  Chart.defaults.font.family = 'Helvetica, sans-serif';
  Chart.defaults.font.size = 10;

  adjustMapBottom();
  fetchWeather();
  fetchKpForecast();
}

export function selectWeatherLocation(coordinate) {
  if (!coordinate) return;
  const [lon, lat] = toLonLat(coordinate);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

  hasSelectedWeatherLocation = true;
  currentCoords = { lat, lon };
  updateLocationDisplay();
  updateCrosshair();
  clearTimeout(debounceTimer);
  fetchWeather();
}

// ── Crosshair overlay ─────────────────────────────────────────
function buildCrosshair(map) {
  const el = document.createElement('div');
  el.id = 'weather-crosshair';
  el.innerHTML = `
    <div class="wc-ring"></div>
    <div class="wc-label">Weather here</div>
  `;
  crosshairOverlay = new Overlay({
    element: el,
    positioning: 'center-center',
    stopEvent: false,
  });
  map.addOverlay(crosshairOverlay);
  updateCrosshair();
}

function updateCrosshair() {
  if (!crosshairOverlay) return;
  const pos = fromLonLat([currentCoords.lon, currentCoords.lat]);
  crosshairOverlay.setPosition((isExpanded || hasSelectedWeatherLocation) ? pos : undefined);
  const label = crosshairOverlay.getElement().querySelector('.wc-label');
  if (label) {
    label.textContent = formatCoords();
  }
}

function formatCoords() {
  const latSuffix = currentCoords.lat >= 0 ? 'N' : 'S';
  const lonSuffix = currentCoords.lon >= 0 ? 'E' : 'W';
  return `${Math.abs(currentCoords.lat).toFixed(2)}°${latSuffix} ${Math.abs(currentCoords.lon).toFixed(2)}°${lonSuffix}`;
}

function updateLocationDisplay() {
  const location = document.getElementById('ws-location');
  if (location) location.textContent = formatCoords();
}

function weatherForecastUrl(lat, lon) {
  const apiLang = (typeof lang === 'string' && lang) ? lang : 'en';
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
  });
  return `/${apiLang}/api/weather/forecast?${params}`;
}

// ── HTML scaffold ─────────────────────────────────────────────
function buildWeatherBarHTML() {
  const bar = document.getElementById('weather-bar');
  bar.innerHTML = `
    <div id="weather-summary">
      <span id="ws-icon">⏳</span>
      <span id="ws-score" class="ws-score">-- --</span>
      <span id="ws-temp" class="ws-item">--°C</span>
      <span id="ws-wind" class="ws-item">💨 --</span>
      <span id="ws-cloud" class="ws-item ws-hide-sm">☁ --%</span>
      <span id="ws-uv" class="ws-item ws-hide-sm" data-pro="true">☀ UV --</span>
      <span id="ws-kp" class="ws-item ws-kp-badge">🧲 Kp --</span>
      <span id="ws-location" class="ws-location">--</span>
      <button id="btn-info" class="ws-icon-btn" title="Weather data guide">ℹ</button>
      <button id="weather-toggle" class="ws-toggle">▲ Weather</button>
    </div>
    <div id="weather-detail">
      <div id="weather-controls" class="wr-controls">
        <div id="day-nav" class="wr-day-nav"></div>
        <div class="wr-drone-btns" id="drone-class-btns">
          <button class="wr-drone-btn wr-mode-active" data-class="dji_mini"  title="DJI Mini 2 / 3 / 4 Pro — under 249g, Level 5 wind (10.7 m/s)">Mini</button>
          <button class="wr-drone-btn" data-class="dji_air"   title="DJI Air 2S / Air 3 — ~700g, Level 6 wind (13.4 m/s)">Air</button>
          <button class="wr-drone-btn" data-class="dji_mavic" title="DJI Mavic 3 / Classic / Pro — ~900g, Level 6 wind (12 m/s)">Mavic</button>
          <button class="wr-drone-btn" data-class="fpv5inch"  title="5-inch freestyle/racing FPV — high wind tolerance, not waterproof">FPV 5"</button>
          <button class="wr-drone-btn" data-class="avata"     title="DJI Avata 1/2 — cinewhoop ~400g, Level 5 wind (10.7 m/s)">Avata</button>
        </div>
        <div class="wr-mode-btns">
          <button id="btn-table" class="wr-mode-btn wr-mode-active">☰ Table</button>
          <button id="btn-chart" class="wr-mode-btn">📈 Charts</button>
        </div>
      </div>
      <div id="weather-table-wrap">
        <div id="weather-detail-inner" class="wr-table"></div>
      </div>
      <div id="weather-chart-wrap" style="display:none;"></div>
    </div>
  `;

  document.getElementById('weather-toggle').addEventListener('click', e => { e.stopPropagation(); toggleWeather(); });
  document.getElementById('weather-summary').addEventListener('click', toggleWeather);
  document.getElementById('btn-info').addEventListener('click', e => { e.stopPropagation(); showInfoModal(); });
  document.getElementById('btn-table').addEventListener('click', e => { e.stopPropagation(); switchMode('table'); });
  document.getElementById('btn-chart').addEventListener('click', e => { e.stopPropagation(); switchMode('chart'); });
  document.getElementById('drone-class-btns').addEventListener('click', e => {
    e.stopPropagation();
    const btn = e.target.closest('[data-class]');
    if (!btn) return;
    droneClass = btn.dataset.class;
    document.querySelectorAll('.wr-drone-btn').forEach(b => b.classList.toggle('wr-mode-active', b === btn));
    renderSummary();
    if (isExpanded) renderCurrent();
  });

  const scoreEl = document.getElementById('ws-score');
  scoreEl.addEventListener('mouseenter', e => {
    e.stopPropagation();
    if (!weatherData) return;
    showScoreTooltip(scoreEl, weatherData.hourly, getCurrentHourIndex());
  });
  scoreEl.addEventListener('mouseleave', hideScoreTooltip);
  scoreEl.addEventListener('click', e => e.stopPropagation());

  // Touch: tap score badge to pin/unpin the tooltip
  let tooltipPinned = false;
  scoreEl.addEventListener('touchstart', e => {
    e.stopPropagation();
    e.preventDefault();
    if (!weatherData) return;
    tooltipPinned = !tooltipPinned;
    tooltipPinned
      ? showScoreTooltip(scoreEl, weatherData.hourly, getCurrentHourIndex())
      : hideScoreTooltip();
  }, { passive: false });
  document.addEventListener('touchstart', e => {
    if (tooltipPinned && scoreTooltipEl
        && !scoreEl.contains(e.target)
        && !scoreTooltipEl.contains(e.target)) {
      tooltipPinned = false;
      hideScoreTooltip();
    }
  });

  const tableContainer = document.getElementById('weather-detail-inner');
  tableContainer.addEventListener('mouseover', e => {
    const cell = e.target.closest('[data-hidx]');
    if (!cell || !weatherData) return;
    showScoreTooltip(cell, weatherData.hourly, parseInt(cell.dataset.hidx, 10));
  });
  tableContainer.addEventListener('mouseout', e => {
    const cell = e.target.closest('[data-hidx]');
    if (cell && !cell.contains(e.relatedTarget)) hideScoreTooltip();
  });
}

// ── Info modal ────────────────────────────────────────────────
function buildInfoModal() {
  const el = document.createElement('div');
  el.id = 'weather-info-modal';
  el.innerHTML = `
    <div id="weather-info-box">
      <button id="weather-info-close">✕</button>
      <h3>Weather Data Guide for Drone Pilots</h3>
      <div class="wi-body">${INFO_HTML}</div>
    </div>
  `;
  document.body.appendChild(el);
  document.getElementById('weather-info-close').addEventListener('click', hideInfoModal);
  el.addEventListener('click', e => { if (e.target === el) hideInfoModal(); });
}

function showInfoModal() { document.getElementById('weather-info-modal').style.display = 'flex'; }
function hideInfoModal() { document.getElementById('weather-info-modal').style.display = 'none'; }

const INFO_HTML = `
<div class="wi-entry"><span class="wi-ico">🌡</span><div>
  <strong>Temperature</strong>
  <p>Air temperature at 2m (ground level), 80m, 120m, 180m, and estimated 445m altitude. Temperature drops ~0.65°C per 100m (standard lapse rate). Below 0°C battery capacity drops sharply.</p>
  <small>Typical CH range: −20°C to +40°C</small>
</div></div>

<div class="wi-entry"><span class="wi-ico">💨</span><div>
  <strong>Wind Speed & Direction</strong>
  <p>Wind speed at takeoff/landing height (10m), 80m, 120m, 180m, and estimated 445m. Wind typically increases with altitude. Arrow shows direction wind blows toward.</p>
  <small class="wi-scale">🟢 0–15 km/h Safe &nbsp;·&nbsp; 🟡 15–25 Caution &nbsp;·&nbsp; 🟠 25–40 Difficult &nbsp;·&nbsp; 🔴 40+ Avoid</small>
</div></div>

<div class="wi-entry"><span class="wi-ico">☁</span><div>
  <strong>Cloud Cover</strong>
  <p>Percentage of sky covered by clouds. High cloud cover reduces visibility and natural lighting. Visual line-of-sight rules prohibit flying above clouds.</p>
  <small>0% = clear sky &nbsp;·&nbsp; 100% = fully overcast</small>
</div></div>

<div class="wi-entry"><span class="wi-ico">🌧</span><div>
  <strong>Rain Probability</strong>
  <p>Chance of precipitation in the forecast hour. Most drones are not waterproof. Rain reduces visibility, risks electronics, and degrades lift efficiency.</p>
  <small class="wi-scale">🟢 &lt;20% Fine &nbsp;·&nbsp; 🟡 20–50% Uncertain &nbsp;·&nbsp; 🔴 &gt;50% Avoid</small>
</div></div>

<div class="wi-entry"><span class="wi-ico">☀</span><div>
  <strong>UV Index</strong>
  <p>Intensity of ultraviolet solar radiation reaching the surface. High UV causes heating of dark drone surfaces. Typical peak in Switzerland: UV 6–8 in summer.</p>
  <small class="wi-scale">🟢 0–2 Low &nbsp;·&nbsp; 🟡 3–5 Moderate &nbsp;·&nbsp; 🟠 6–7 High &nbsp;·&nbsp; 🔴 8–10 Very High &nbsp;·&nbsp; 🟣 11+ Extreme</small>
</div></div>

<div class="wi-entry"><span class="wi-ico">⚡</span><div>
  <strong>Solar Radiation (Shortwave)</strong>
  <p>Total solar energy reaching the ground in W/m². Affects camera glare, surface heating, and in some conditions thermal updrafts. Clear noon peaks near 1000 W/m².</p>
  <small>0 W/m² at night &nbsp;·&nbsp; up to ~1000 W/m² peak noon</small>
</div></div>

<div class="wi-entry"><span class="wi-ico">🧲</span><div>
  <strong>Kp Index — Geomagnetic Activity</strong>
  <p>Measures disturbances in Earth's magnetic field from solar wind. High Kp directly degrades GPS positioning accuracy and makes compass-based heading unreliable — critical for drone navigation and return-to-home.</p>
  <table class="wi-kp-table">
    <tr><td style="color:#4ade80">Kp 0–1</td><td>Quiet</td><td>GPS &amp; compass fully reliable</td></tr>
    <tr><td style="color:#a3e635">Kp 2–3</td><td>Unsettled</td><td>Minor effects possible</td></tr>
    <tr><td style="color:#facc15">Kp 4</td><td>Active</td><td>Slight compass deviation</td></tr>
    <tr><td style="color:#fb923c">Kp 5</td><td>G1 Minor storm</td><td>GPS accuracy may decrease</td></tr>
    <tr><td style="color:#f97316">Kp 6</td><td>G2 Moderate</td><td>Compass unreliable, GPS degraded</td></tr>
    <tr><td style="color:#ef4444">Kp 7</td><td>G3 Strong</td><td>Significant GPS errors likely</td></tr>
    <tr><td style="color:#dc2626">Kp 8–9</td><td>G4–G5 Severe</td><td>GPS may fail — avoid flying</td></tr>
  </table>
  <small>~ prefix = NOAA predicted value (not yet observed) &nbsp;·&nbsp; Updated every 15 min</small>
</div></div>
`;

// ── Layout ────────────────────────────────────────────────────
function expandedH() {
  if (displayMode === 'chart') {
    const chromeH = COLLAPSED_H + 34 + 20 + (CHART_COUNT * 22) + ((CHART_COUNT - 1) * 10);
    const targetH = chromeH + (CHART_COUNT * CHART_MIN_H);
    return Math.min(targetH, Math.floor(window.innerHeight * 0.88));
  }
  return Math.min(TABLE_H, Math.floor(window.innerHeight * 0.72));
}

function adjustMapBottom() {
  const h = isExpanded ? expandedH() : COLLAPSED_H;
  document.getElementById('weather-bar').style.height = h + 'px';
  document.getElementById('map').style.bottom = h + 'px';
  if (mapRef) mapRef.updateSize();
}

// ── Data fetching ─────────────────────────────────────────────
async function fetchWeather() {
  const requestId = ++weatherRequestId;
  const { lat, lon } = currentCoords;
  const icon = document.getElementById('ws-icon');
  if (icon) icon.textContent = '⏳';
  const url = weatherForecastUrl(lat, lon);
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error();
    const data = await res.json();
    if (requestId !== weatherRequestId) return;
    weatherData = data;
    renderSummary();
    if (isExpanded) {
      renderCurrent();
    } else if (!localStorage.getItem('wx_seen')) {
      localStorage.setItem('wx_seen', '1');
      toggleWeather();
      renderCurrent();
    }
  } catch {
    if (requestId !== weatherRequestId) return;
    const el = document.getElementById('ws-icon');
    if (el) el.textContent = '⚠️';
  }
}

async function fetchKpForecast() {
  try {
    const res = await fetch('https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json');
    if (!res.ok) throw new Error();
    kpData = await res.json();
    renderSummary();
    if (isExpanded) renderCurrent();
  } catch { /* silently degrade */ }
  clearTimeout(kpRefreshTimer);
  kpRefreshTimer = setTimeout(fetchKpForecast, KP_REFRESH_MS);
}

// ── Kp helpers ────────────────────────────────────────────────
function getCurrentKp() {
  if (!kpData || !kpData.length) return null;
  const now = Date.now();
  let best = null, bestDiff = Infinity;
  for (const e of kpData) {
    const d = Math.abs(now - new Date(e.time_tag + 'Z').getTime());
    if (d < bestDiff) { bestDiff = d; best = e; }
  }
  return best;
}

function kpForLocalTime(localTimeStr) {
  if (!kpData || !kpData.length) return null;
  const target = new Date(localTimeStr).getTime();
  let best = null, bestDiff = Infinity;
  for (const e of kpData) {
    const d = Math.abs(target - new Date(e.time_tag + 'Z').getTime());
    if (d < bestDiff) { bestDiff = d; best = e; }
  }
  return best;
}

// ── Color / icon helpers ──────────────────────────────────────
function windArrow(deg) {
  return ['↓','↙','←','↖','↑','↗','→','↘'][Math.round(deg / 45) % 8];
}
function windColor(s) {
  return s < 15 ? '#4ade80' : s < 25 ? '#facc15' : s < 40 ? '#f97316' : '#ef4444';
}
function cloudColor(p) {
  return p < 25 ? '#93c5fd' : p < 60 ? '#60a5fa' : '#94a3b8';
}
function uvColor(u) {
  return u <= 2 ? '#4ade80' : u <= 5 ? '#facc15' : u <= 7 ? '#f97316' : '#ef4444';
}
function rainColor(p) {
  return p < 20 ? '#93c5fd' : p < 50 ? '#3b82f6' : '#1d4ed8';
}
function kpColor(k) {
  if (k == null) return '#666';
  return k < 2 ? '#4ade80' : k < 4 ? '#a3e635' : k < 5 ? '#facc15' : k < 6 ? '#fb923c' : k < 7 ? '#f97316' : k < 8 ? '#ef4444' : '#dc2626';
}
function weatherIcon(cloud, isDay, precip) {
  if (precip > 60) return '🌧';
  if (cloud < 20) return isDay ? '☀️' : '🌙';
  if (cloud < 50) return '⛅';
  return '☁️';
}

function getCurrentHourIndex() {
  if (!weatherData) return 0;
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const target = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:00`;
  const idx = weatherData.hourly.time.indexOf(target);
  return idx >= 0 ? idx : 0;
}

// ── Summary bar ───────────────────────────────────────────────
function renderSummary() {
  if (!weatherData) return;
  const h = weatherData.hourly;
  const i = getCurrentHourIndex();
  document.getElementById('ws-icon').textContent = weatherIcon(h.cloudcover[i], h.is_day[i], h.precipitation_probability[i]);
  const score = flightScore(h, i);
  const scoreEl = document.getElementById('ws-score');
  if (scoreEl) {
    scoreEl.textContent = SCORE_LABELS[score];
    scoreEl.style.color = SCORE_COLORS[score];
    scoreEl.style.borderColor = SCORE_COLORS[score] + '66';
    scoreEl.style.background = SCORE_COLORS[score] + '18';
  }
  document.getElementById('ws-temp').textContent = `${Math.round(h.temperature_2m[i])}°C`;
  document.getElementById('ws-wind').textContent = `💨 ${Math.round(h.windspeed_10m[i])} km/h ${windArrow(h.winddirection_10m[i])}`;
  document.getElementById('ws-cloud').textContent = `☁ ${Math.round(h.cloudcover[i])}%`;
  document.getElementById('ws-uv').textContent = `☀ UV ${h.uv_index[i].toFixed(1)}`;
  updateLocationDisplay();
  const kpEl = document.getElementById('ws-kp');
  const cur = getCurrentKp();
  if (cur) {
    kpEl.textContent = `🧲 Kp ${cur.kp.toFixed(1)}${cur.noaa_scale ? ' ' + cur.noaa_scale : ''}`;
    kpEl.style.color = kpColor(cur.kp);
  }
}

// ── Dispatch render based on mode ─────────────────────────────
function renderCurrent() {
  if (displayMode === 'table') renderTable();
  else renderCharts();
}

// ── Day navigation ────────────────────────────────────────────
function renderDayNav() {
  const nav = document.getElementById('day-nav');
  if (!weatherData || !nav) return;
  const h = weatherData.hourly;
  const si = getCurrentHourIndex();
  nav.innerHTML = '';

  // Collect day-boundary column offsets
  const days = [{ col: 0, label: 'Today' }];
  for (let ci = 1; ci < Math.min(HOURS_TO_SHOW, h.time.length - si); ci++) {
    if (h.time[si + ci] && h.time[si + ci].endsWith('T00:00')) {
      const d = new Date(h.time[si + ci]);
      const names = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      days.push({ col: ci, label: `${names[d.getDay()]} ${d.getDate()}` });
    }
  }

  days.forEach(({ col, label }, idx) => {
    const btn = document.createElement('button');
    btn.className = 'wr-day-btn' + (idx === 0 ? ' wr-mode-active' : '');
    btn.textContent = label;
    btn.addEventListener('click', () => {
      document.getElementById('weather-table-wrap').scrollLeft = col * 52;
      nav.querySelectorAll('.wr-day-btn').forEach(b => b.classList.remove('wr-mode-active'));
      btn.classList.add('wr-mode-active');
    });
    nav.appendChild(btn);
  });
}

function series(h, ...keys) {
  for (const key of keys) {
    if (Array.isArray(h?.[key])) return h[key];
  }
  return null;
}

function valueAt(h, key, i) {
  return series(h, key)?.[i] ?? null;
}

function estimatedTemp445(h, i) {
  const t180 = valueAt(h, 'temperature_180m', i);
  if (t180 != null) return t180 - ((445 - 180) * TEMP_LAPSE_C_PER_M);
  const t2 = valueAt(h, 'temperature_2m', i);
  return t2 != null ? t2 - ((445 - 2) * TEMP_LAPSE_C_PER_M) : null;
}

function estimatedWind445(h, i) {
  const w180 = valueAt(h, 'windspeed_180m', i) ?? valueAt(h, 'wind_speed_180m', i);
  if (w180 != null) return w180 * Math.pow(445 / 180, WIND_SHEAR_EXPONENT);
  const w120 = valueAt(h, 'windspeed_120m', i) ?? valueAt(h, 'wind_speed_120m', i);
  return w120 != null ? w120 * Math.pow(445 / 120, WIND_SHEAR_EXPONENT) : null;
}

function windDirAt(h, level, i) {
  return valueAt(h, `winddirection_${level}`, i) ?? valueAt(h, `wind_direction_${level}`, i);
}

// ── Table mode ────────────────────────────────────────────────
function renderTable() {
  if (!weatherData) return;
  renderDayNav();
  const h = weatherData.hourly;
  const si = getCurrentHourIndex();
  const indices = Array.from({ length: HOURS_TO_SHOW }, (_, k) => si + k).filter(i => i < h.time.length);

  function makeLabel(timeStr, ci) {
    if (ci === 0) return 'NOW';
    const t = timeStr.split('T')[1]; // "14:00"
    if (t === '00:00') {
      const d = new Date(timeStr);
      return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
    }
    return t;
  }

  const rows = [
    { label: '',        cells: indices.map((i,ci) => makeLabel(h.time[i], ci)), colorFn: null,  pro: false, isTime: true },
    { label: '🛸 Score', cells: indices.map(i => SCORE_DOTS[flightScore(h, i)]), colorFn: ci => SCORE_COLORS[flightScore(h, si+ci)], pro: false, isScore: true },
    { label: '🌡 2m',   cells: indices.map(i => `${Math.round(h.temperature_2m[i])}°`),  colorFn: null, pro: false },
    { label: '🌡 80m',  cells: indices.map(i => `${Math.round(h.temperature_80m[i])}°`), colorFn: null, pro: true  },
    { label: '🌡 120m', cells: indices.map(i => `${Math.round(h.temperature_120m[i])}°`),colorFn: null, pro: true  },
    { label: '🌡 180m', cells: indices.map(i => valueAt(h, 'temperature_180m', i) != null ? `${Math.round(valueAt(h, 'temperature_180m', i))}°` : '--'), colorFn: null, pro: true },
    { label: '🌡 445m*', cells: indices.map(i => estimatedTemp445(h, i) != null ? `${Math.round(estimatedTemp445(h, i))}°` : '--'), colorFn: null, pro: true },
    { label: '💨 10m',  cells: indices.map(i => `${Math.round(valueAt(h, 'windspeed_10m', i) ?? valueAt(h, 'wind_speed_10m', i))} ${windArrow(windDirAt(h, '10m', i))}`),  colorFn: ci => windColor(valueAt(h, 'windspeed_10m', si+ci) ?? valueAt(h, 'wind_speed_10m', si+ci)),  pro: false },
    { label: '💨 80m',  cells: indices.map(i => `${Math.round(valueAt(h, 'windspeed_80m', i) ?? valueAt(h, 'wind_speed_80m', i))} ${windArrow(windDirAt(h, '80m', i))}`),  colorFn: ci => windColor(valueAt(h, 'windspeed_80m', si+ci) ?? valueAt(h, 'wind_speed_80m', si+ci)),  pro: true  },
    { label: '💨 120m', cells: indices.map(i => `${Math.round(valueAt(h, 'windspeed_120m', i) ?? valueAt(h, 'wind_speed_120m', i))} ${windArrow(windDirAt(h, '120m', i))}`),colorFn: ci => windColor(valueAt(h, 'windspeed_120m', si+ci) ?? valueAt(h, 'wind_speed_120m', si+ci)), pro: true  },
    { label: '💨 180m', cells: indices.map(i => (valueAt(h, 'windspeed_180m', i) ?? valueAt(h, 'wind_speed_180m', i)) != null ? `${Math.round(valueAt(h, 'windspeed_180m', i) ?? valueAt(h, 'wind_speed_180m', i))} ${windArrow(windDirAt(h, '180m', i))}` : '--'), colorFn: ci => (valueAt(h, 'windspeed_180m', si+ci) ?? valueAt(h, 'wind_speed_180m', si+ci)) != null ? windColor(valueAt(h, 'windspeed_180m', si+ci) ?? valueAt(h, 'wind_speed_180m', si+ci)) : '#666', pro: true },
    { label: '💨 445m*', cells: indices.map(i => estimatedWind445(h, i) != null ? `${Math.round(estimatedWind445(h, i))} ${windArrow(windDirAt(h, '180m', i))}` : '--'), colorFn: ci => estimatedWind445(h, si+ci) != null ? windColor(estimatedWind445(h, si+ci)) : '#666', pro: true },
    { label: '☁ Cloud', cells: indices.map(i => `${Math.round(h.cloudcover[i])}%`),              colorFn: ci => cloudColor(h.cloudcover[si+ci]),            pro: false },
    { label: '🌧 Rain',  cells: indices.map(i => `${Math.round(h.precipitation_probability[i])}%`),colorFn: ci => rainColor(h.precipitation_probability[si+ci]),pro: false },
    { label: '☀ UV',    cells: indices.map(i => h.uv_index[i].toFixed(1)),                        colorFn: ci => uvColor(h.uv_index[si+ci]),                 pro: true  },
    { label: '⚡ Solar', cells: indices.map(i => `${Math.round(h.shortwave_radiation[i])}W`),      colorFn: null,                                             pro: true  },
    {
      label: '🧲 Kp',
      cells: indices.map(i => {
        const e = kpForLocalTime(h.time[i]);
        if (!e) return '--';
        return `${e.observed === 'predicted' ? '~' : ''}${e.kp.toFixed(1)}${e.noaa_scale ? ' '+e.noaa_scale : ''}`;
      }),
      colorFn: ci => { const e = kpForLocalTime(h.time[si+ci]); return e ? kpColor(e.kp) : '#666'; },
      pro: false, isKp: true,
    },
  ];

  const container = document.getElementById('weather-detail-inner');
  container.innerHTML = '';

  rows.forEach(({ label, cells, colorFn, pro, isTime, isKp, isScore }) => {
    const row = document.createElement('div');
    row.className = 'wr-row' + (pro ? ' wr-pro' : '') + (isKp ? ' wr-kp-row' : '') + (isScore ? ' wr-score-row' : '');

    const lbl = document.createElement('div');
    lbl.className = 'wr-label';
    if (pro) {
      const badge = document.createElement('span');
      badge.className = 'wr-pro-badge';
      badge.textContent = 'PRO';
      lbl.textContent = label + ' ';
      lbl.appendChild(badge);
    } else {
      lbl.textContent = label;
    }
    row.appendChild(lbl);

    cells.forEach((val, ci) => {
      const cell = document.createElement('div');
      const isMidnight = !isTime && weatherData && h.time[si+ci] && h.time[si+ci].endsWith('T00:00');
      cell.className = 'wr-cell' + (ci === 0 ? ' wr-now' : '') + (isMidnight ? ' wr-midnight' : '');
      cell.textContent = val;
      if (colorFn) cell.style.color = colorFn(ci);
      if (isScore) { cell.dataset.hidx = String(si + ci); cell.style.cursor = 'help'; }
      if (isTime) {
        cell.style.fontSize = ci === 0 ? '0.72em' : '0.68em';
        cell.style.color = ci === 0 ? '#fff' : (val.length <= 5 ? '#888' : '#b0c4de');
        if (ci === 0 || val.length > 5) cell.style.fontWeight = 'bold';
      }
      row.appendChild(cell);
    });

    container.appendChild(row);
  });
}

// ── Chart mode ────────────────────────────────────────────────

const nowLinePlugin = {
  id: 'nowLine',
  afterDraw(chart) {
    if (!chart.data._nowIdx && chart.data._nowIdx !== 0) return;
    const ctx = chart.ctx;
    const { top, bottom } = chart.chartArea;
    const nowIdx = chart.data._nowIdx;
    const firstPoint = chart.getDatasetMeta(0)?.data?.[nowIdx];
    const x = firstPoint?.x ?? chart.scales.x.getPixelForValue(chart.data.labels[nowIdx], nowIdx);
    if (!Number.isFinite(x)) return;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, bottom); ctx.stroke();
    ctx.restore();
  }
};

const hoverLinePlugin = {
  id: 'hoverLine',
  afterDraw(chart) {
    const dataIndex = chart.$syncDataIndex;
    if (dataIndex == null) return;

    const firstPoint = chart.getDatasetMeta(0)?.data?.[dataIndex];
    const x = firstPoint?.x ?? chart.scales.x.getPixelForValue(chart.data.labels[dataIndex], dataIndex);
    if (!Number.isFinite(x)) return;

    const ctx = chart.ctx;
    const { top, bottom } = chart.chartArea;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.42)';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  }
};

const kpBandPlugin = {
  id: 'kpBand',
  beforeDraw(chart) {
    // Use y2 if Kp is on the secondary axis (chart 4), otherwise y
    const yScale = chart.scales.y2 || chart.scales.y;
    if (!yScale) return;
    const ctx = chart.ctx;
    const { left, right } = chart.chartArea;
    [
      [0, 2, 'rgba(74,222,128,0.07)'],
      [2, 4, 'rgba(163,230,53,0.07)'],
      [4, 5, 'rgba(250,204,21,0.09)'],
      [5, 6, 'rgba(251,146,60,0.1)'],
      [6, 9, 'rgba(239,68,68,0.1)'],
    ].forEach(([from, to, color]) => {
      const y1 = yScale.getPixelForValue(to);
      const y2 = yScale.getPixelForValue(from);
      ctx.fillStyle = color;
      ctx.fillRect(left, y1, right - left, y2 - y1);
    });
  }
};

// Draws small wind-direction arrows along the top of the chart area
function makeWindArrowPlugin(dirKey, windDatasetIdx) {
  return {
    id: `windArrows_${_wpId++}`,
    afterDatasetsDraw(chart) {
      const dirData = chart.data[dirKey];
      if (!dirData) return;
      const meta = chart.getDatasetMeta(windDatasetIdx);
      if (!meta?.data?.length) return;
      const ctx = chart.ctx;
      const { top, left: al, right: ar } = chart.chartArea;
      const n = meta.data.length;
      const step = Math.max(3, Math.round(n / ((ar - al) / 28)));
      meta.data.forEach((pt, i) => {
        if (i % step !== 0) return;
        const dir = dirData[i];
        if (dir == null) return;
        const x = pt.x;
        if (x < al || x > ar) return;
        ctx.save();
        ctx.translate(x, top + 11);
        ctx.rotate(((dir + 180) * Math.PI) / 180); // arrow → direction wind blows TO
        const sz = 5;
        ctx.fillStyle = 'rgba(255,255,255,0.62)';
        ctx.strokeStyle = 'rgba(0,0,0,0.45)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, -sz);
        ctx.lineTo(sz * 0.45, sz * 0.6);
        ctx.lineTo(0, sz * 0.2);
        ctx.lineTo(-sz * 0.45, sz * 0.6);
        ctx.closePath();
        ctx.fill(); ctx.stroke();
        ctx.restore();
      });
    }
  };
}

function degreesToCompass(deg) {
  return ['N','NE','E','SE','S','SW','W','NW'][Math.round(deg / 45) % 8];
}

let _wpId = 0;

function destroyCharts() {
  if (pendingRaf !== null) { cancelAnimationFrame(pendingRaf); pendingRaf = null; }
  chartInstances.forEach(c => c.destroy());
  chartInstances = [];
}

function addSection(container, title, canvasId, h = 155) {
  const sec = document.createElement('div');
  sec.className = 'wr-chart-section';
  sec.innerHTML = `<div class="wr-chart-title">${title}</div><div class="wr-chart-wrap" style="height:${h}px;position:relative"><canvas id="${canvasId}" style="height:${h}px"></canvas></div>`;
  container.appendChild(sec);
  return document.getElementById(canvasId);
}

const X_SCALE = {
  grid: { color: 'rgba(255,255,255,0.05)' },
  ticks: { color: '#777', maxRotation: 0, autoSkip: true, maxTicksLimit: 22, font: { size: 11 } },
};
const TOOLTIP_STYLE = {
  backgroundColor: 'rgba(10,10,18,0.95)', borderColor: '#3a3a5c',
  borderWidth: 1, titleColor: '#fff', bodyColor: '#bbb', padding: 6,
  titleFont: { size: 12 }, bodyFont: { size: 12 },
};
const LEGEND_STYLE = {
  display: true, position: 'top', align: 'end',
  labels: { color: '#aaa', boxWidth: 11, padding: 8, font: { size: 11 } },
};

function syncChartHover(sourceChart, activeElements) {
  if (syncingCharts) return;
  syncingCharts = true;

  const active = activeElements?.[0];
  if (!active) {
    chartInstances.forEach(chart => {
      chart.$syncDataIndex = null;
      chart.$syncTooltipX = null;
      chart.$syncTooltipY = null;
      chart.setActiveElements([]);
      chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
      chart.update('none');
    });
    syncingCharts = false;
    return;
  }

  const dataIndex = active.index;
  chartInstances.forEach(chart => {
    const chartActive = chart.data.datasets
      .map((_, datasetIndex) => ({ datasetIndex, index: dataIndex }))
      .filter(({ datasetIndex, index }) => chart.getDatasetMeta(datasetIndex)?.data?.[index]);

    const xPoint = chart.getDatasetMeta(0)?.data?.[dataIndex]?.x;
    const x = xPoint ?? chart.scales.x.getPixelForValue(chart.data.labels[dataIndex], dataIndex);
    const y = chart.chartArea.top + ((chart.chartArea.bottom - chart.chartArea.top) / 2);
    chart.$syncDataIndex = dataIndex;
    chart.$syncTooltipX = x;
    chart.$syncTooltipY = y;
    chart.setActiveElements(chartActive);
    chart.tooltip?.setActiveElements(chartActive, { x, y });
    chart.update('none');
  });

  syncingCharts = false;
}

const pointRadiusForHover = ctx => (ctx.chart.$syncDataIndex === ctx.dataIndex ? 5 : 0);
const pointBorderWidthForHover = ctx => (ctx.chart.$syncDataIndex === ctx.dataIndex ? 2 : 0);
const pointStyleForHover = {
  pointRadius: pointRadiusForHover,
  pointBorderWidth: pointBorderWidthForHover,
  pointBackgroundColor: '#0b0b12',
  pointHoverRadius: 6,
};

function mkOpts({ y = {}, y2 = null, si = 0, tooltipCallbacks = {} } = {}) {
  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    interaction: { mode: 'index', intersect: false },
    hover: { mode: 'index', intersect: false },
    onHover(event, activeElements, chart) {
      syncChartHover(chart, activeElements);
    },
    plugins: {
      legend: LEGEND_STYLE,
      tooltip: {
        ...TOOLTIP_STYLE,
        callbacks: {
          title([ctx]) {
            const h = weatherData?.hourly;
            if (!h) return ctx.label;
            const t = h.time[si + ctx.dataIndex];
            if (!t) return ctx.label;
            const d = new Date(t);
            const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}  ${t.split('T')[1]}`;
          },
          ...tooltipCallbacks,
        },
        position: 'synced',
      },
    },
    scales: {
      x: X_SCALE,
      y: {
        position: 'left',
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: y.color || '#888', font: { size: 11 } },
        title: y.label ? { display: true, text: y.label, color: y.color || '#666', font: { size: 11 } } : { display: false },
        ...(y.min !== undefined ? { min: y.min } : {}),
        ...(y.max !== undefined ? { max: y.max } : {}),
      },
    },
  };
  if (y2) {
    opts.scales.y2 = {
      position: 'right',
      grid: { drawOnChartArea: false },
      ticks: { color: y2.color || '#666', font: { size: 11 } },
      title: y2.label ? { display: true, text: y2.label, color: y2.color || '#666', font: { size: 11 } } : { display: false },
      ...(y2.min !== undefined ? { min: y2.min } : {}),
      ...(y2.max !== undefined ? { max: y2.max } : {}),
    };
  }
  return opts;
}

function renderCharts() {
  _wpId = 0;
  destroyCharts();
  if (!weatherData) return;
  const wrap = document.getElementById('weather-chart-wrap');
  wrap.innerHTML = '';

  const h = weatherData.hourly;
  const si = getCurrentHourIndex();
  const indices = Array.from({ length: HOURS_TO_SHOW }, (_, k) => si + k).filter(i => i < h.time.length);

  const barH = expandedH();
  const perChartH = Math.max(CHART_MIN_H, Math.min(CHART_MAX_H, Math.floor((barH - 236) / CHART_COUNT)));

  const labels = indices.map(i => {
    const [, time] = h.time[i].split('T');
    if (time === '00:00') {
      const d = new Date(h.time[i]);
      return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()] + ' ' + d.getDate();
    }
    return time;
  });

  const ld = (label, data, color, extra = {}) => ({
    label, data, borderColor: color, backgroundColor: color + '18',
    borderWidth: 1.5, ...pointStyleForHover, tension: 0.3, fill: false, ...extra,
  });

  const withDirTooltip = (dirData, windDsIdx) => ({
    afterLabel(ctx) {
      if (ctx.datasetIndex !== windDsIdx) return '';
      const dir = dirData[ctx.dataIndex];
      if (dir == null) return '';
      return `  ${windArrow(dir)} from ${degreesToCompass(dir)} (${Math.round(dir)}°)`;
    },
  });

  // Pre-compute data arrays
  const dir10  = indices.map(i => windDirAt(h, '10m', i));
  const dir80  = indices.map(i => windDirAt(h, '80m', i));
  const dir120 = indices.map(i => windDirAt(h, '120m', i));
  const dir180 = indices.map(i => windDirAt(h, '180m', i));
  const temp180 = indices.map(i => valueAt(h, 'temperature_180m', i));
  const wind180 = indices.map(i => valueAt(h, 'windspeed_180m', i) ?? valueAt(h, 'wind_speed_180m', i));
  const temp445 = indices.map(i => estimatedTemp445(h, i));
  const wind445 = indices.map(i => estimatedWind445(h, i));
  const kpVals = indices.map((_, ci) => { const e = kpForLocalTime(h.time[si+ci]); return e ? e.kp : null; });
  const kpPred = indices.map((_, ci) => { const e = kpForLocalTime(h.time[si+ci]); return e ? e.observed === 'predicted' : false; });

  // Build all four chart configs (pure data — no DOM touches)
  const defs = [
    {
      id: 'ch-1', title: '🌡 2m Temp  ·  💨 10m Wind',
      cfg: {
        type: 'line',
        data: { _nowIdx: 0, labels, _dir: dir10, datasets: [
          ld('Temp 2m (°C)', indices.map(i => valueAt(h, 'temperature_2m', i)), '#e2e8f0', { yAxisID: 'y' }),
          { label: 'Wind 10m (km/h)', data: indices.map(i => valueAt(h, 'windspeed_10m', i) ?? valueAt(h, 'wind_speed_10m', i)), borderWidth: 2, ...pointStyleForHover, tension: 0.3, fill: false, yAxisID: 'y2', segment: { borderColor: c => windColor(c.p1.parsed.y) }, borderColor: '#4ade80' },
          ld('Gusts (km/h)', indices.map(i => valueAt(h, 'windgusts_10m', i) ?? valueAt(h, 'wind_gusts_10m', i)), '#ef4444', { borderDash: [2,3], borderWidth: 1, yAxisID: 'y2', fill: true, backgroundColor: 'rgba(239,68,68,0.06)' }),
        ]},
        options: mkOpts({ y: { label: '°C', color: '#cbd5e1' }, y2: { label: 'km/h', color: '#4ade80', min: 0 }, si, tooltipCallbacks: withDirTooltip(dir10, 1) }),
        plugins: [nowLinePlugin, hoverLinePlugin, makeWindArrowPlugin('_dir', 1)],
      },
    },
    {
      id: 'ch-2', title: '🌡 80m Temp  ·  💨 80m Wind  PRO',
      cfg: {
        type: 'line',
        data: { _nowIdx: 0, labels, _dir: dir80, datasets: [
          ld('Temp 80m (°C)', indices.map(i => valueAt(h, 'temperature_80m', i)), '#67e8f9', { yAxisID: 'y' }),
          { label: 'Wind 80m (km/h)', data: indices.map(i => valueAt(h, 'windspeed_80m', i) ?? valueAt(h, 'wind_speed_80m', i)), borderWidth: 2, ...pointStyleForHover, tension: 0.3, fill: false, yAxisID: 'y2', segment: { borderColor: c => windColor(c.p1.parsed.y) }, borderColor: '#facc15' },
        ]},
        options: mkOpts({ y: { label: '°C', color: '#67e8f9' }, y2: { label: 'km/h', color: '#facc15', min: 0 }, si, tooltipCallbacks: withDirTooltip(dir80, 1) }),
        plugins: [nowLinePlugin, hoverLinePlugin, makeWindArrowPlugin('_dir', 1)],
      },
    },
    {
      id: 'ch-3', title: '🌡 120m Temp  ·  💨 120m Wind  PRO',
      cfg: {
        type: 'line',
        data: { _nowIdx: 0, labels, _dir: dir120, datasets: [
          ld('Temp 120m (°C)', indices.map(i => valueAt(h, 'temperature_120m', i)), '#93c5fd', { yAxisID: 'y' }),
          { label: 'Wind 120m (km/h)', data: indices.map(i => valueAt(h, 'windspeed_120m', i) ?? valueAt(h, 'wind_speed_120m', i)), borderWidth: 2, ...pointStyleForHover, tension: 0.3, fill: false, yAxisID: 'y2', segment: { borderColor: c => windColor(c.p1.parsed.y) }, borderColor: '#f97316' },
        ]},
        options: mkOpts({ y: { label: '°C', color: '#93c5fd' }, y2: { label: 'km/h', color: '#f97316', min: 0 }, si, tooltipCallbacks: withDirTooltip(dir120, 1) }),
        plugins: [nowLinePlugin, hoverLinePlugin, makeWindArrowPlugin('_dir', 1)],
      },
    },
    {
      id: 'ch-4', title: '🌡 180m Temp  ·  💨 180m Wind  PRO',
      cfg: {
        type: 'line',
        data: { _nowIdx: 0, labels, _dir: dir180, datasets: [
          ld('Temp 180m (°C)', temp180, '#c4b5fd', { yAxisID: 'y' }),
          { label: 'Wind 180m (km/h)', data: wind180, borderWidth: 2, ...pointStyleForHover, tension: 0.3, fill: false, yAxisID: 'y2', segment: { borderColor: c => windColor(c.p1.parsed.y) }, borderColor: '#a78bfa' },
        ]},
        options: mkOpts({ y: { label: '°C', color: '#c4b5fd' }, y2: { label: 'km/h', color: '#a78bfa', min: 0 }, si, tooltipCallbacks: withDirTooltip(dir180, 1) }),
        plugins: [nowLinePlugin, hoverLinePlugin, makeWindArrowPlugin('_dir', 1)],
      },
    },
    {
      id: 'ch-5', title: '🌡 445m Temp  ·  💨 445m Wind  EST',
      cfg: {
        type: 'line',
        data: { _nowIdx: 0, labels, _dir: dir180, datasets: [
          ld('Temp 445m est (°C)', temp445, '#f0abfc', { yAxisID: 'y' }),
          { label: 'Wind 445m est (km/h)', data: wind445, borderWidth: 2, ...pointStyleForHover, tension: 0.3, fill: false, yAxisID: 'y2', segment: { borderColor: c => windColor(c.p1.parsed.y) }, borderColor: '#d946ef' },
        ]},
        options: mkOpts({ y: { label: '°C', color: '#f0abfc' }, y2: { label: 'km/h', color: '#d946ef', min: 0 }, si, tooltipCallbacks: withDirTooltip(dir180, 1) }),
        plugins: [nowLinePlugin, hoverLinePlugin, makeWindArrowPlugin('_dir', 1)],
      },
    },
    {
      id: 'ch-6', title: '☁ Cloud · 🌧 Rain · ⚡ Solar · ☀ UV · 🧲 Kp',
      cfg: {
        type: 'line',
        data: { _nowIdx: 0, labels, datasets: [
          { label: 'Cloud %',  data: indices.map(i => h.cloudcover[i]),                borderColor: '#94a3b8', backgroundColor: 'rgba(148,163,184,0.13)', borderWidth: 1.5, ...pointStyleForHover, tension: 0.3, fill: true, yAxisID: 'y' },
          { label: 'Rain %',   data: indices.map(i => h.precipitation_probability[i]), borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.1)',   borderWidth: 1.5, ...pointStyleForHover, tension: 0.3, fill: true, yAxisID: 'y' },
          { label: 'Solar/10', data: indices.map(i => h.shortwave_radiation[i] / 10),  borderColor: '#fb923c33', backgroundColor: 'rgba(251,146,60,0.08)', borderWidth: 1, ...pointStyleForHover, tension: 0.3, fill: true, yAxisID: 'y' },
          { label: 'UV Index', data: indices.map(i => h.uv_index[i]), borderWidth: 2, ...pointStyleForHover, tension: 0.3, fill: false, yAxisID: 'y2', segment: { borderColor: c => uvColor(c.p1.parsed.y) }, borderColor: '#facc15' },
          { label: 'Kp', data: kpVals, borderWidth: 2, ...pointStyleForHover, tension: 0.2, fill: false, yAxisID: 'y2', segment: { borderColor: c => kpColor(c.p1.parsed.y), borderDash: c => kpPred[c.p1.dataIndex] ? [4,3] : [] }, borderColor: '#a3e635' },
        ]},
        options: mkOpts({
          y: { label: '%', color: '#94a3b8', min: 0, max: 100 },
          y2: { label: 'Index', color: '#aaa', min: 0, max: 12 },
          si,
          tooltipCallbacks: { label(ctx) { return ctx.dataset.label === 'Solar/10' ? `Solar: ${Math.round(ctx.raw * 10)} W/m²` : `${ctx.dataset.label}: ${ctx.formattedValue}`; } },
        }),
        plugins: [nowLinePlugin, hoverLinePlugin, kpBandPlugin],
      },
    },
  ];

  // ── Step 1: insert all canvas sections into DOM ───────────────
  defs.forEach(({ id, title }) => addSection(wrap, title, id, perChartH));

  // ── Step 2: explicit canvas sizing, then create Chart.js instances ─
  // Chart.js reads clientWidth/Height from the canvas parent. Set both CSS and
  // backing-store sizes so sections below the fold do not initialize as blank.
  pendingRaf = requestAnimationFrame(() => {
    pendingRaf = null;
    const w = Math.max(300, wrap.clientWidth - 16);
    defs.forEach(({ id, cfg }) => {
      const canvas = document.getElementById(id);
      if (!canvas) return;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${perChartH}px`;
      canvas.width  = w;
      canvas.height = perChartH;
      chartInstances.push(new Chart(canvas, {
        ...cfg,
        options: { ...cfg.options, responsive: true, maintainAspectRatio: false },
      }));
    });
  });
}

// ── Mode switching ────────────────────────────────────────────
function switchMode(mode) {
  displayMode = mode;
  document.getElementById('weather-table-wrap').style.display = mode === 'table' ? 'block' : 'none';
  document.getElementById('weather-chart-wrap').style.display = mode === 'chart' ? 'block' : 'none';
  document.getElementById('btn-table').classList.toggle('wr-mode-active', mode === 'table');
  document.getElementById('btn-chart').classList.toggle('wr-mode-active', mode === 'chart');
  adjustMapBottom();
  renderCurrent();
}

export function toggleWeather() {
  isExpanded = !isExpanded;
  const detail = document.getElementById('weather-detail');
  const btn = document.getElementById('weather-toggle');
  const bar = document.getElementById('weather-bar');

  if (isExpanded) {
    detail.style.display = 'flex';
    btn.textContent = '▼ Close';
    bar.style.height = expandedH() + 'px';
    renderCurrent();
  } else {
    detail.style.display = 'none';
    btn.textContent = '▲ Weather';
    bar.style.height = COLLAPSED_H + 'px';
    destroyCharts();
  }

  updateCrosshair();
  adjustMapBottom();
}
